import * as Utils from '../lib/utils';
import { BackendObjectRef } from './backend-integration.service';
import { FormFieldType } from './form-field-type.model';

export interface FormFieldVisibility {
    visibility: 'visible'|'hidden';
    target: string;
    operator: string;
    value: string|string[];
}

export class FormField {
    private static ID_PREFIX = 'wff-';
    public id: string;

    public availableValues?: BackendObjectRef[];
    public roles?: BackendObjectRef[];
    public content?: string;
    public name: string;
    public required: boolean;
    public type: FormFieldType;
    public visibility?: FormFieldVisibility;
    public asyncValidators: string[];
    public syncValidators: string[];

    constructor(id: string|undefined,
                name: string,
                type: FormFieldType,
                required: boolean,
                visibility: FormFieldVisibility|undefined,
                values?: BackendObjectRef[],
                roles?: BackendObjectRef[],
                content?: string,
                asyncValidators?: string[],
                syncValidators?: string[]) {
        this.id = id ?? `${FormField.ID_PREFIX}${Utils.randomString()}`;
        this.name = name;
        this.type = type;
        this.required = required;
        this.visibility = visibility;
        this.availableValues = values;
        this.roles = roles;
        this.content = content;
        this.asyncValidators = asyncValidators ?? [];
        this.syncValidators = syncValidators ?? [];
    }

    public copy(): FormField {
        return new FormField(this.id,
                             this.name,
                             this.type,
                             this.required,
                             this.visibility,
                             this.availableValues,
                             this.roles,
                             this.content,
                             this.asyncValidators,
                             this.syncValidators);
    }

    public patch(value: Partial<FormField>): FormField {
        this.name = value.name ?? this.name;
        this.required = value.required ?? this.required;
        this.type = value.type ?? this.type;

        const keys = Object.keys(value);
        this.visibility =
            keys.includes('visibility') ? value.visibility : this.visibility;
        this.availableValues =
            keys.includes('availableValues') ? value.availableValues : this.availableValues;
        this.roles =
            keys.includes('roles') ? value.roles : this.roles;
        this.content =
            keys.includes('content') ? value.content : this.content;

        return this;
    }

    /**
     * Returns field label for the UI
     *
     * Label depends on the type of the field and can be either `name`
     * or `content`.
     *
     * @param maxLength Maximum length of the resulting string excluding
     *                  ellipsis. Pass -1 to turn off truncating.
     */
    public label(maxLength = -1, ellipsis = '...'): string {
        let label = this.name || '';
        if (this.type.isContentRequired) { label = this.content ?? ''; }
        if (maxLength === -1 || label.length < maxLength) { return label; }
        return label.slice(0, maxLength) + ellipsis;
    }
}
